import { I18n } from "@assets/strings";
import React from "react";
import { ClipLoader } from "react-spinners";
import { ApplicationType } from "@domain/application/application";
import { ApplicationDraft } from "@domain/application/applicationDraft";
import { useApplication, useDegreeLevel } from "@domain/application/hooks";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { NotFoundPage } from "@ui/pages/notFoundPage";
import { ApplicationEditor } from "./applicationEditor";

interface ApplicationEditorPageProps extends React.HTMLAttributes<HTMLDivElement> {
	applicationId?: string;
	type?: ApplicationType;
}
export const ApplicationEditorPage: React.FC<ApplicationEditorPageProps> = ({ applicationId, type, ...htmlProps }) =>
	applicationId ? (
		<ApplicationUpdatePage applicationId={applicationId} {...htmlProps} />
	) : (
		<ApplicationCreationPage type={type} {...htmlProps} />
	);

interface ApplicationUpdatePageProps extends React.HTMLAttributes<HTMLDivElement> {
	applicationId: string;
}
const ApplicationUpdatePage: React.FC<ApplicationUpdatePageProps> = ({ applicationId }) => {
	const { result: application, loading, error } = useApplication(applicationId);
	const { result: levels } = useDegreeLevel();

	if (error?.code === 404) {
		return <NotFoundPage message={I18n.t("404.quiz")} />;
	}

	return (
		<>
			{loading ? (
				<ClipLoader />
			) : (
				application && (
					<ApplicationEditor initialApplication={{ ...application, levels: levels }} applicationId={applicationId} />
				)
			)}
		</>
	);
};

interface ApplicationCreatePageProps extends React.HTMLAttributes<HTMLDivElement> {
	type?: ApplicationType;
}
const ApplicationCreationPage: React.FC<ApplicationCreatePageProps> = ({ type, ...htmlProps }) => {
	const currentProfile = useCurrentProfile();
	const { result: levels, loading } = useDegreeLevel();

	const initialApplication: ApplicationDraft = {
		firstName: currentProfile?.primaryName ?? "",
		familyName: currentProfile?.secondaryName ?? "",
		dateBirth: currentProfile?.birthDate ?? new Date(),
		countryResidence: undefined,
		nationality: undefined,
		email: currentProfile?.email ?? "",
		phoneNumber: "",
		idPassport: undefined,
		proofResidence: undefined,
		selfDeclarationFinancialNeed: undefined,
		videoId: undefined,
		resume: undefined,
		diploma: undefined,
		transcriptRecord: undefined,
		workCertificate: undefined,
		englishCertificat: undefined,
		isCertificate: undefined,
		degreeId: undefined,
		levelId: undefined,
		credit: undefined,
		credits: null,
		desiredStartDate: new Date(),
		levels,
		programSlug: "omat",
		degrees: null,
		degree: { name: "", id: "", credits: [] },
		level: { name: "", id: "" },
		hasAlreadyVoted: false,
		voteCount: 0,
	};

	return loading ? <ClipLoader /> : <ApplicationEditor {...htmlProps} initialApplication={initialApplication} />;
};
